import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppActivityRules } from "./AppActivity";
import { AppContact, AppIndex, AppRow } from "./AppIndex";
import { AppTaskRules } from "./AppTaskRules";

// AppOverview - the main screen's Overview tab (owner-approved #3, 2026-09-27): who the owner is most in
// touch with (last 30 / 90 days), who has gone quiet against their own rhythm, and who is owed a reply.
// ⛔ NO AI AND NO GUESS: every number is the service's exact count (`index.json` `act`, `ActivityIndex`),
// and "gone quiet" is `AppActivityRules.IsQuiet` - the SAME rule the service uses for the Tasks tab's
// "Reconnect" suggestions, so the two lists cannot disagree.
//
// ⚠ RULES ONLY - pure, no DOM, no transport (`AppOverviewView` draws; `AppMain` wires). A second consumer
// (another product's "people" screen) passes different contacts; the selection rules stay here.

/** One line of an Overview list: the contact and the number it is ranked by. */
export class AppOverviewEntry {
    public Contact: AppContact = new AppContact();
    /** Messages in the window (Most in touch), the monthly level (Gone quiet), or days waiting (owed). */
    public Count: number = 0;
    /** The sort key, largest first: `Count` for most-first lists, `-Count` for fewest-first (`AppOverview.Ranked`). */
    public Rank: number = 0;
}

export class AppOverview {
    public static readonly Route: string = "overview";
    /** Lines per list - a glance, not a report; the Chats tab holds everybody. */
    public static readonly Max: number = 8;
    /** The one period "Most in touch" covers (owner, 2026-10-14: the 30/90 switch was removed - nobody used it). */
    public static readonly Days: number = 90;
    private static readonly Names: Intl.Collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

    /** Does this index carry the activity numbers at all? False = an older index: the tab says so. */
    public static HasNumbers = (Index: AppIndex): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            for (const One of Index.Contacts) {
                if (One.Act !== null) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.HasNumbers");
            return false;
        }
    };

    /**
     * The contacts with the most messages in the last `Days` (90), most first, ties by name; only
     * contacts with at least one message there. ⛔ 1:1 only.
     */
    public static Top = (Index: AppIndex): AppOverviewEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppOverviewEntry[] = [];
            for (const One of Index.Contacts) {
                if (One.IsGroup === true || One.Act === null) { continue; }
                const Count: number = One.Act.D90;
                if (Count <= 0) { continue; }
                Out.push(AppOverview.Entry(One, Count, true));
            }
            return AppOverview.Ranked(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.Top");
            return [];
        }
    };

    /** Who has gone quiet (`IsQuiet`), the biggest usual level first - the silence most out of character. */
    public static Quiet = (Index: AppIndex): AppOverviewEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppOverviewEntry[] = [];
            for (const One of Index.Contacts) {
                if (One.IsGroup === true || One.Act === null) { continue; }
                if (AppActivityRules.IsQuiet(One.Act, One.Standing, One.DaysSinceLast) === false) { continue; }
                Out.push(AppOverview.Entry(One, AppActivityRules.Monthly(One.Act), true));
            }
            return AppOverview.Ranked(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.Quiet");
            return [];
        }
    };

    /**
     * Who the owner owes a reply, newest first (the Tasks tab's "Replies owed" order). ⭐ With tasks on,
     * the OPEN reply rows (`r-`) - so a reply the owner snoozed or dismissed is not listed here either;
     * an index without the task fields falls back to the contacts whose standing is `owed`.
     */
    public static Owed = (Index: AppIndex, Today: string): AppOverviewEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppOverviewEntry[] = [];
            const Tasks: boolean = AppTaskRules.Enabled(Index);
            const Open: Record<string, boolean> = AppOverview.OpenReplies(Index.Tasks, Today);
            for (const One of Index.Contacts) {
                if (One.IsGroup === true) { continue; }
                if (Tasks === true && Open[One.Href] !== true) { continue; }
                if (Tasks === false && One.Standing !== "owed") { continue; }
                Out.push(AppOverview.Entry(One, One.DaysSinceLast, false));
            }
            return AppOverview.Ranked(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.Owed");
            return [];
        }
    };

    /** The hrefs of contacts with an OPEN reply-owed task row on `Today`. */
    private static OpenReplies = (Rows: AppRow[], Today: string): Record<string, boolean> => {
        if (getCoreUtils().KErrorMode === true) { return {}; }
        try {
            const Out: Record<string, boolean> = {};
            for (const Row of Rows) {
                if (AppTaskRules.IsReply(Row) === true && AppTaskRules.IsOpen(Row, Today) === true) { Out[Row.Href] = true; }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.OpenReplies");
            return {};
        }
    };

    /** The largest count in a list (the bar scale), at least 1. */
    public static MaxOf = (Rows: AppOverviewEntry[]): number => {
        if (getCoreUtils().KErrorMode === true) { return 1; }
        try {
            return Rows.reduce((Most: number, One: AppOverviewEntry): number => {
                return One.Count > Most ? One.Count : Most;
            }, 1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.MaxOf");
            return 1;
        }
    };

    /** DOCUMENTED EXCEPTION: builds a value from two in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    /** DOCUMENTED EXCEPTION: builds a value. `MostFirst` false = the smallest count leads (its rank is `-Count`).
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static Entry = (Contact: AppContact, Count: number, MostFirst: boolean): AppOverviewEntry => {
        const Out: AppOverviewEntry = new AppOverviewEntry();
        Out.Contact = Contact;
        Out.Count = Count;
        Out.Rank = Count;
        if (MostFirst === false) { Out.Rank = -Count; }
        return Out;
    };

    /** ⭐ THE ONE ORDER-AND-CUT for every list here: by rank, ties by name, the first `Max`. */
    private static Ranked = (Rows: AppOverviewEntry[]): AppOverviewEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            Rows.sort(AppOverview.ByRank);
            return Rows.slice(0, AppOverview.Max);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOverview.Ranked");
            return [];
        }
    };

    /**
     * DOCUMENTED EXCEPTION: a comparator - the larger rank first, then the name (natural order). The direction rides in
     * the rank (`Entry`), so one comparator serves most-first and fewest-first.
     * ERROR-SUPPRESSED-JUSTIFIED: pure and total; reporting from inside `sort` would report per comparison.
     */
    private static ByRank = (A: AppOverviewEntry, B: AppOverviewEntry): number => {
        if (A.Rank !== B.Rank) { return B.Rank - A.Rank; }
        return AppOverview.Names.compare(A.Contact.Title, B.Contact.Title);
    };
}
